import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

export type DropdownOption = { id: string; label: string; disabled?: boolean };
const motionDuration = 150;

export function Dropdown({
  options,
  selected,
  onChange,
  multiple = false,
  disabled = false,
  placeholder = 'Выберите значение',
  className = '',
  minPopupWidth = 200,
  label,
  id,
  required,
  invalid,
  buttonRef: suppliedRef,
}: {
  options: DropdownOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  minPopupWidth?: number;
  label?: string;
  id?: string;
  required?: boolean;
  invalid?: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}) {
  const fallbackRef = useRef<HTMLButtonElement>(null);
  const buttonRef = suppliedRef || fallbackRef;
  const popupRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 200,
    maxHeight: 300,
    above: false,
  });
  const visible = options
    .filter((option) => !(option.disabled && option.id === ''))
    .filter((option) =>
      option.label.toLocaleLowerCase('ru').includes(search.toLocaleLowerCase('ru')),
    );
  const chosen = options.filter((option) => selected.includes(option.id));

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setMounted(false);
      setSearch('');
    }, motionDuration);
    return () => window.clearTimeout(timer);
  }, [open]);

  useLayoutEffect(() => {
    if (!mounted) return;
    const reposition = () => {
      const box = buttonRef.current?.getBoundingClientRect();
      if (!box) return;
      const width = Math.min(Math.max(box.width, minPopupWidth), window.innerWidth - 32);
      const below = window.innerHeight - box.bottom - 16;
      const above = box.top - 16;
      const upwards = below < Math.min(412, options.length * 44 + 64) && above > below;
      setPosition({
        left: Math.max(16, Math.min(box.left, window.innerWidth - width - 16)),
        top: upwards ? box.top - 8 : box.bottom + 8,
        width,
        maxHeight: Math.max(96, Math.min(412, upwards ? above - 8 : below - 8)),
        above: upwards,
      });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [mounted, buttonRef, options.length, minPopupWidth]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (
        !buttonRef.current?.contains(event.target as Node) &&
        !popupRef.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open, buttonRef]);

  useEffect(() => {
    if (open)
      document.getElementById(`${menuId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, menuId, open, mounted]);

  const reveal = () => {
    if (disabled) return;
    setActive(
      Math.max(
        0,
        visible.findIndex((option) => selected.includes(option.id) && !option.disabled),
      ),
    );
    setOpen(true);
  };
  const choose = (option: DropdownOption) => {
    if (disabled || option.disabled) return;
    onChange(
      multiple
        ? selected.includes(option.id)
          ? selected.filter((value) => value !== option.id)
          : [...selected, option.id]
        : [option.id],
    );
    if (!multiple) {
      setOpen(false);
      buttonRef.current?.focus();
    }
  };
  const keydown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (!open) {
        reveal();
        return;
      }
      const enabled = visible
        .map((option, index) => (option.disabled ? -1 : index))
        .filter((index) => index >= 0);
      const current = enabled.indexOf(active);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next =
        event.key === 'Home'
          ? enabled[0]
          : event.key === 'End'
            ? enabled.at(-1)
            : current < 0
              ? step > 0
                ? enabled[0]
                : enabled.at(-1)
              : enabled[(current + step + enabled.length) % enabled.length];
      if (next !== undefined) setActive(next);
      return;
    }
    if (
      (event.key === 'Enter' &&
        (event.target === buttonRef.current || event.target instanceof HTMLInputElement)) ||
      (event.key === ' ' && event.target === buttonRef.current)
    ) {
      event.preventDefault();
      if (!open) reveal();
      else if (visible[active]) choose(visible[active]!);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-activedescendant={open && visible[active] ? `${menuId}-${active}` : undefined}
        aria-required={required}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        className={`select-trigger ${className}`}
        onClick={() => (open ? setOpen(false) : reveal())}
        onKeyDown={keydown}
      >
        <span>{chosen.length ? chosen.map((option) => option.label).join(', ') : placeholder}</span>
        <Icon name="ChevronDown" size={16} />
      </button>
      {mounted &&
        createPortal(
          <div
            ref={popupRef}
            className={`dropdown-position ${position.above ? 'above' : ''}`}
            style={{ top: position.top, left: position.left, width: position.width }}
          >
            <div
              className={`dropdown-popover ${open ? 'is-open' : 'is-closing'}`}
              aria-hidden={!open}
              onKeyDown={keydown}
            >
              {options.length > 6 && (
                <div className="dropdown-search">
                  <Icon name="Search" size={16} />
                  <input
                    aria-label="Поиск в списке"
                    placeholder="Поиск"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setActive(0);
                    }}
                  />
                </div>
              )}
              <div
                role="listbox"
                id={menuId}
                aria-label={label}
                aria-multiselectable={multiple || undefined}
                className="dropdown-options"
                style={{ maxHeight: position.maxHeight - (options.length > 6 ? 60 : 16) }}
              >
                {visible.map((option, index) => (
                  <button
                    type="button"
                    role="option"
                    id={`${menuId}-${index}`}
                    key={option.id}
                    aria-selected={selected.includes(option.id)}
                    disabled={disabled || option.disabled}
                    data-active={active === index || undefined}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => choose(option)}
                  >
                    <span>{option.label}</span>
                    {selected.includes(option.id) && <Icon name="CheckSmall" size={16} />}
                  </button>
                ))}
                {!visible.length && <p className="muted">Нет подходящих вариантов</p>}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function textContent(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  return Children.toArray(node)
    .map((child) =>
      isValidElement<{ children?: ReactNode }>(child)
        ? textContent(child.props.children)
        : typeof child === 'string' || typeof child === 'number'
          ? String(child)
          : '',
    )
    .join('');
}
function optionList(children: ReactNode, parentDisabled = false): DropdownOption[] {
  return Children.toArray(children).flatMap((child) => {
    if (
      !isValidElement<{
        value?: string | number;
        label?: string;
        disabled?: boolean;
        children?: ReactNode;
      }>(child)
    )
      return [];
    if (child.type === 'option')
      return [
        {
          id: String(child.props.value ?? textContent(child.props.children)),
          label: child.props.label || textContent(child.props.children),
          disabled: parentDisabled || child.props.disabled,
        },
      ];
    return optionList(child.props.children, parentDisabled || child.props.disabled);
  });
}
const values = (value: SelectHTMLAttributes<HTMLSelectElement>['value']) =>
  value === undefined ? [] : Array.isArray(value) ? value.map(String) : [String(value)];

export function Select({
  children,
  className = '',
  onChange,
  id,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  const options = optionList(children);
  const selectRef = useRef<HTMLSelectElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [uncontrolled, setUncontrolled] = useState(() =>
    values(props.defaultValue ?? (props.multiple ? undefined : options[0]?.id)),
  );
  const [invalid, setInvalid] = useState(false);
  const selected = props.value === undefined ? uncontrolled : values(props.value);
  return (
    <span className="select-control">
      <Dropdown
        options={options}
        selected={selected}
        multiple={props.multiple}
        disabled={props.disabled}
        className={className}
        id={id}
        label={props['aria-label']}
        required={props.required}
        invalid={invalid}
        buttonRef={buttonRef}
        onChange={(next) => {
          const select = selectRef.current;
          if (!select) return;
          for (const option of Array.from(select.options))
            option.selected = next.includes(option.value);
          setInvalid(false);
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }}
      />
      <select
        {...props}
        id={undefined}
        ref={selectRef}
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          setUncontrolled(Array.from(event.target.selectedOptions, (option) => option.value));
          onChange?.(event);
        }}
        onInvalid={(event) => {
          event.preventDefault();
          setInvalid(true);
          buttonRef.current?.focus();
          props.onInvalid?.(event);
        }}
      >
        {children}
      </select>
    </span>
  );
}
